import React from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import type { VisitStatus } from '@app-types/reception';

interface FiltersBarProps {
  filters: {
    status?: VisitStatus[];
    doctorId?: string;
    search?: string;
  };
  onFiltersChange: (filters: any) => void;
  doctors?: Array<{ id: string; firstName: string; lastName: string }>;
}

const statusOptions: Array<{ value: VisitStatus; label: string }> = [
  { value: 'scheduled', label: 'Programat' },
  { value: 'arrived', label: 'Sosit' },
  { value: 'in_consultation', label: 'In consultatie' },
  { value: 'finished', label: 'Finalizat' },
  { value: 'canceled', label: 'Anulat' },
  { value: 'no_show', label: 'Absent' }
];

export const FiltersBar: React.FC<FiltersBarProps> = ({ 
  filters, 
  onFiltersChange,
  doctors = []
}) => {
  const handleStatusChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      status: typeof value === 'string' ? value.split(',') : value
    });
  };

  const handleDoctorChange = (event: SelectChangeEvent<string>) => {
    onFiltersChange({
      ...filters,
      doctorId: event.target.value || undefined
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      search: event.target.value
    });
  };

  const handleClearSearch = () => {
    onFiltersChange({
      ...filters,
      search: ''
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      status: undefined,
      doctorId: undefined,
      search: ''
    });
  };

  const hasActiveFilters = 
    (filters.status && filters.status.length > 0) || 
    filters.doctorId || 
    filters.search;

  return (
    <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
      {/* Cautare text */}
      <TextField
        placeholder="Cauta animal, proprietar..."
        value={filters.search || ''}
        onChange={handleSearchChange}
        size="small"
        sx={{ minWidth: 250 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: filters.search && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClearSearch}>
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      {/* Filtrare dupa status */}
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Status</InputLabel>
        <Select
          multiple
          value={filters.status || []}
          onChange={handleStatusChange}
          input={<OutlinedInput label="Status" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected as string[]).map((value) => {
                const option = statusOptions.find(opt => opt.value === value);
                return <Chip key={value} label={option?.label} size="small" />;
              })}
            </Box>
          )}
        >
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Filtrare dupa medic */}
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Medic</InputLabel>
        <Select
          value={filters.doctorId || ''}
          onChange={handleDoctorChange}
          label="Medic"
        >
          <MenuItem value="">
            <em>Toti medicii</em>
          </MenuItem>
          {doctors.map((doctor) => (
            <MenuItem key={doctor.id} value={doctor.id}>
              Dr. {doctor.firstName} {doctor.lastName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Buton pentru a sterge toate filtrele */}
      {hasActiveFilters && (
        <Chip
          label="Sterge filtre"
          onDelete={clearAllFilters}
          color="default"
          variant="outlined"
        />
      )}
    </Box>
  );
};

